import { SubStatus, Tabs, UseDesktopFlight } from './types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterFareGroups } from '../Results/utils';
import { Props } from './components/DesktopFlight';
import { useConfig } from '../context';
import { FareFamilyPriceFragment, FlightClass } from '@websky/graphql';
import { getCheapestFlightInCombination } from './utils';

export const useDesktopFlight: UseDesktopFlight = (componentProps: Props) => {
	const { selectedFares, flight, isPreOpen, onSelect } = componentProps;

	const { showAvailableFaresOnly } = useConfig().Engine;

	const flightBlock = useRef<HTMLDivElement>(null);
	const [activeTab, openTab] = useState<Tabs>(null);

	const lastSelectedFare = useMemo(() => {
		return selectedFares?.[selectedFares.length - 1]?.fareGroupId ?? null;
	}, [selectedFares]);

	// #fixme: вынести во Flight
	const economyFareGroups = filterFareGroups(
		flight.pricesForFareGroups,
		selectedFares ?? [],
		FlightClass.Economy,
		showAvailableFaresOnly
	);

	const businessFareGroups = filterFareGroups(
		flight.pricesForFareGroups,
		selectedFares ?? [],
		FlightClass.Business,
		showAvailableFaresOnly
	);

	const milesFareGroups = filterFareGroups(
		flight.pricesForFareGroups,
		selectedFares ?? [],
		FlightClass.Miles,
		showAvailableFaresOnly
	);

	const filteredPrices = useMemo(() => {
		return economyFareGroups.concat(businessFareGroups);
	}, [economyFareGroups, businessFareGroups]);

	const subStatuses = useMemo(() => {
		const subStatusSet = new Set<SubStatus>();

		flight.segments.forEach(segment => {
			if (segment?.segment?.subStatus) {
				const subStatus = segment.segment.subStatus as SubStatus;

				subStatusSet.add(subStatus);
			}
		});

		return [...subStatusSet];
	}, [flight.segments]);

	const flightSegments = useMemo(() => {
		if (!subStatuses.includes('Upgrade') && !subStatuses.includes('AwaitingExchange')) {
			return flight.segments;
		}

		const segmentsWithoutExchangeOrUpgrade = flight.segments.filter(segment => {
			const subStatus = segment?.segment?.subStatus as SubStatus;

			if (!subStatus) {
				return true;
			}

			return !['Upgrade', 'AwaitingExchange'].includes(subStatus);
		});

		// если все сегменты перелёта находятся в обмене/повышении класса, фильтрация обнулит массив -
		// в этом случае показываем исходные сегменты, чтобы не терять данные о перелёте
		return segmentsWithoutExchangeOrUpgrade.length > 0 ? segmentsWithoutExchangeOrUpgrade : flight.segments;
	}, [flight.segments, subStatuses]);

	useEffect(() => {
		activeTab
			? setTimeout(() => {
					flightBlock?.current?.scrollIntoView({ behavior: 'smooth' });
			  }, 120)
			: null;
	}, [activeTab]);

	useEffect(() => {
		if (isPreOpen) {
			if (economyFareGroups.some(fareGroup => fareGroup.prices)) {
				openTab(Tabs.EconomyFares);
			} else if (businessFareGroups.some(fareGroup => fareGroup.prices)) {
				openTab(Tabs.BusinessFares);
			}
		}
	}, [isPreOpen]);

	const onOpenFlightInfoTab = useCallback((tab: Tabs) => () => openTab(active => (active !== tab ? tab : null)), []);

	const getFareGroupTabHandler = useCallback(
		(serviceClass: FlightClass) => {
			if (serviceClass === FlightClass.Business) {
				return onOpenFlightInfoTab(Tabs.BusinessFares);
			}

			return onOpenFlightInfoTab(Tabs.EconomyFares);
		},
		[onOpenFlightInfoTab]
	);

	// #fixme: this thing needs to be isolated
	const getOnFareSelect = (group: FareFamilyPriceFragment) => {
		return () => {
			if (onSelect) {
				const cheapestFlight = getCheapestFlightInCombination(group, selectedFares);
				const cheapestFlightId = cheapestFlight?.flight?.id;

				const faresIds = new Set<string>();

				group.flights.forEach(flight => {
					if (flight.id === cheapestFlightId) {
						flight.fares?.forEach(fare => faresIds.add(fare.id));
					}
				});

				onSelect({
					fareGroupId: group.fareFamily.id,
					fareFamily: group.fareFamily,
					fareName: group.fareFamily.title,
					prices: group.prices,
					flightInfo: {
						id: cheapestFlightId,
						segments: flight.segments
					},
					faresIds: [...faresIds]
				});

				openTab(null);
			}
		};
	};

	return {
		// data
		lastSelectedFare,
		subStatuses,
		flightSegments,
		flightBlock,
		activeTab,
		flight,
		filteredPrices,
		economyFareGroups,
		businessFareGroups,
		milesFareGroups,

		// methods
		getOnFareSelect,
		getFareGroupTabHandler,
		handleFlightInfoTab: onOpenFlightInfoTab(Tabs.FlightInfo),
		handleEconomyFareGroupsTab: onOpenFlightInfoTab(Tabs.EconomyFares),
		handleBusinessFareGroupsTab: onOpenFlightInfoTab(Tabs.BusinessFares),
		handleMilesFareGroupsTab: onOpenFlightInfoTab(Tabs.MilesFares)
	};
};
